import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

import { API_RESPONSE_STATUS } from '../constants/api-response-status.constants';
import {
  API_RESPONSE_MESSAGE_KEY,
  DEFAULT_SERVER_ERROR_MESSAGE,
  ERROR_CODE_MESSAGES,
  ERROR_EXCEPTION_CODES,
  ERROR_MESSAGE_SEPARATOR,
  HTTP_STATUS,
  HttpStatusCode,
  REST_API_FAILURE_HTTP_STATUS,
  RUNTIME_MESSAGE_PREFERRED_CODES,
  SERVER_ERROR_MESSAGES,
  STRING_TYPE,
  toErrorMessageLookupKey
} from '../constants/error.constants';

type GraphQLErrorExtension = Readonly<{
  code?: string;
  status?: number;
}>;

type GraphQLErrorItem = Readonly<{
  message?: string;
  extensions?: GraphQLErrorExtension;
}>;

export type ApiErrorBody = Readonly<{
  status?: string;
  exception_code?: string;
  message?: string | null;
  errors?: readonly GraphQLErrorItem[];
}>;

const isString = (value: unknown): value is string => typeof value === STRING_TYPE;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasNonEmptyMessage = (message?: string | null): message is string =>
  isString(message) && message.trim().length > 0;

const capitalizeMessage = (message: string): string => {
  const trimmedMessage = message.trim();
  if (!trimmedMessage) {
    return trimmedMessage;
  }
  return `${trimmedMessage.charAt(0).toUpperCase()}${trimmedMessage.slice(1)}`;
};

const formatFieldValidationValue = (value: unknown): string | null => {
  if (isString(value)) {
    return hasNonEmptyMessage(value) ? capitalizeMessage(value) : null;
  }
  if (Array.isArray(value)) {
    const messages = value
      .map(validationValue => formatFieldValidationValue(validationValue))
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(ERROR_MESSAGE_SEPARATOR.FIELD) : null;
  }
  if (isRecord(value)) {
    const messages = Object.values(value)
      .map(validationValue => formatFieldValidationValue(validationValue))
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(ERROR_MESSAGE_SEPARATOR.FIELD) : null;
  }
  return null;
};

const formatMutationFieldValidationMessage = (message: Record<string, unknown>): string | null => {
  const messages = Object.values(message)
    .map(value => formatFieldValidationValue(value))
    .filter((item): item is string => Boolean(item));
  return messages.length > 0 ? messages.join(ERROR_MESSAGE_SEPARATOR.FIELD) : null;
};

const resolveMutationValidationMessage = (message: unknown): string => {
  if (isString(message)) {
    return hasNonEmptyMessage(message) ? message : DEFAULT_SERVER_ERROR_MESSAGE;
  }
  if (isRecord(message)) {
    return formatMutationFieldValidationMessage(message) ?? DEFAULT_SERVER_ERROR_MESSAGE;
  }
  return DEFAULT_SERVER_ERROR_MESSAGE;
};

export const extractGraphQLMutationValidationMessage = (body: unknown): string | null => {
  if (!isRecord(body) || !isRecord(body['data'])) {
    return null;
  }

  const mutationPayload = Object.values(body['data']).find(
    payload =>
      isRecord(payload) &&
      API_RESPONSE_MESSAGE_KEY in payload &&
      payload[API_RESPONSE_MESSAGE_KEY] !== API_RESPONSE_STATUS.SUCCESS
  );

  return isRecord(mutationPayload) ? resolveMutationValidationMessage(mutationPayload[API_RESPONSE_MESSAGE_KEY]) : null;
};

export const hasGraphQLMutationValidationError = (body: unknown): boolean =>
  Boolean(extractGraphQLMutationValidationMessage(body));

const getMappedErrorMessage = (code: string, status?: number): string | undefined => {
  const statusSpecificMessage = ERROR_CODE_MESSAGES[toErrorMessageLookupKey(code, status)];
  if (statusSpecificMessage) {
    return statusSpecificMessage;
  }

  return ERROR_CODE_MESSAGES[code];
};

const resolveStatusErrorMessage = (statusCode: number): string =>
  statusCode in SERVER_ERROR_MESSAGES
    ? SERVER_ERROR_MESSAGES[statusCode as HttpStatusCode]
    : DEFAULT_SERVER_ERROR_MESSAGE;

export const normalizeApiErrorBody = (value: unknown): unknown => {
  if (!isString(value)) {
    return value;
  }
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return value;
  }
};

const asApiErrorBody = (value: unknown): ApiErrorBody | null => {
  const normalizedValue = normalizeApiErrorBody(value);
  return isRecord(normalizedValue) ? (normalizedValue as ApiErrorBody) : null;
};

const resolveMessageForCode = (code: string, fallbackMessage?: string | null, status?: number): string | null => {
  if (RUNTIME_MESSAGE_PREFERRED_CODES.has(code) && hasNonEmptyMessage(fallbackMessage)) {
    return fallbackMessage;
  }
  return getMappedErrorMessage(code, status) ?? (hasNonEmptyMessage(fallbackMessage) ? fallbackMessage : null);
};

const resolveRestApiErrorMessage = (
  exceptionCode: string | undefined,
  message: string | null | undefined,
  status?: number
): string | null => {
  if (exceptionCode) {
    return resolveMessageForCode(exceptionCode, message, status);
  }
  return hasNonEmptyMessage(message) ? message : null;
};

const resolveGraphQLErrorMessage = (graphQLError: GraphQLErrorItem): string | null => {
  const code = graphQLError.extensions?.code;
  if (code) {
    return resolveMessageForCode(code, graphQLError.message, graphQLError.extensions?.status);
  }
  return hasNonEmptyMessage(graphQLError.message) ? graphQLError.message : null;
};

const extractGraphQLErrorMessage = (errors: readonly GraphQLErrorItem[]): string => {
  const messages = errors.map(resolveGraphQLErrorMessage).filter((message): message is string => Boolean(message));
  return messages.join(ERROR_MESSAGE_SEPARATOR.MULTI);
};

const extractStatusCodeFromBody = (body: ApiErrorBody): number | undefined => {
  const graphQlStatus = body.errors?.[0]?.extensions?.status;
  return typeof graphQlStatus === 'number' ? graphQlStatus : undefined;
};

export const hasGraphQLErrors = (body: unknown): body is ApiErrorBody & { errors: readonly GraphQLErrorItem[] } => {
  const errors = asApiErrorBody(body)?.errors;
  return Array.isArray(errors) && errors.length > 0;
};

export const isRestApiFailureResponse = (body: unknown): body is ApiErrorBody & { status: string } => {
  const status = asApiErrorBody(body)?.status;
  return isString(status) && status.toLowerCase() === API_RESPONSE_STATUS.FAILURE;
};

export const resolveRestApiFailureHttpStatus = (httpStatus: number): number =>
  httpStatus === HTTP_STATUS.OK ? REST_API_FAILURE_HTTP_STATUS : httpStatus;

export const isApiErrorBody = (body: unknown): boolean =>
  hasGraphQLErrors(body) || isRestApiFailureResponse(body) || hasGraphQLMutationValidationError(body);

export const hasAnyApiErrorInPayload = (body: unknown): boolean => {
  if (Array.isArray(body)) {
    return body.some(item => hasAnyApiErrorInPayload(normalizeApiErrorBody(item)));
  }
  return isApiErrorBody(body);
};

export const extractExceptionCodeFromBody = (body: unknown): string | undefined => {
  const apiErrorBody = asApiErrorBody(body);
  return apiErrorBody?.exception_code ?? apiErrorBody?.errors?.[0]?.extensions?.code;
};

export const extractExceptionCode = (err: HttpErrorResponse): string | undefined =>
  extractExceptionCodeFromBody(err.error);

const isUserInitiatedRequest = (request: HttpRequest<unknown>): boolean =>
  typeof request.body === 'object' &&
  Boolean(request.body) &&
  'userInitiated' in request.body &&
  Boolean((request.body as { userInitiated?: boolean }).userInitiated);

export const shouldSkipCentralizedErrorToast = (
  bodyOrError: HttpErrorResponse | unknown,
  request?: HttpRequest<unknown>
): boolean => {
  const exceptionCode =
    bodyOrError instanceof HttpErrorResponse
      ? extractExceptionCode(bodyOrError)
      : extractExceptionCodeFromBody(bodyOrError);

  if (exceptionCode === ERROR_EXCEPTION_CODES.INVALID_OR_EXPIRED_TOKEN) {
    return true;
  }
  return (
    Boolean(request) &&
    exceptionCode === ERROR_EXCEPTION_CODES.USER_ALREADY_IN_ORGANIZATION &&
    isUserInitiatedRequest(request)
  );
};

export const resolveApiErrorMessage = (body: unknown, httpStatus: number = HTTP_STATUS.OK): string => {
  const apiErrorBody = asApiErrorBody(body);
  if (!apiErrorBody) {
    return resolveStatusErrorMessage(httpStatus);
  }
  const { exception_code: exceptionCode, message, errors } = apiErrorBody;
  if (errors?.length) {
    const graphQLErrorMessage = extractGraphQLErrorMessage(errors);
    if (graphQLErrorMessage) {
      return graphQLErrorMessage;
    }
  }
  const mutationValidationMessage = extractGraphQLMutationValidationMessage(apiErrorBody);
  if (mutationValidationMessage) {
    return mutationValidationMessage;
  }
  const statusCode = extractStatusCodeFromBody(apiErrorBody) ?? httpStatus;
  const restApiErrorMessage = resolveRestApiErrorMessage(exceptionCode, message, statusCode);
  if (restApiErrorMessage) {
    return restApiErrorMessage;
  }
  return resolveStatusErrorMessage(statusCode);
};

export const resolveHttpErrorMessage = (err: HttpErrorResponse): string => {
  const normalizedError = normalizeApiErrorBody(err.error);
  if (isApiErrorBody(normalizedError)) {
    return resolveApiErrorMessage(normalizedError, err.status ?? HTTP_STATUS.OK);
  }
  return resolveStatusErrorMessage(err.status ?? HTTP_STATUS.OK);
};

export const forEachApiErrorBody = (body: unknown, handler: (errorBody: ApiErrorBody) => void): void => {
  if (Array.isArray(body)) {
    body.forEach(item => forEachApiErrorBody(item, handler));
    return;
  }
  const apiErrorBody = asApiErrorBody(body);
  if (apiErrorBody && isApiErrorBody(apiErrorBody)) {
    handler(apiErrorBody);
  }
};
